import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { useUser } from '../contexts/UserContext';
import TicketCard from '../components/TicketCard';
import MultiSelect from '../components/MultiSelect';
import {
  DASHBOARD_TICKET_FILTERS_STORAGE_KEY,
  loadTicketListFilters,
  saveTicketListFilters,
} from '../utils/ticketListFilters';

const Dashboard = () => {
  const initialFilters = loadTicketListFilters(DASHBOARD_TICKET_FILTERS_STORAGE_KEY);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilters, setStatusFilters] = useState(initialFilters.status);
  const [categoryFilters, setCategoryFilters] = useState(initialFilters.category);
  const [creatorFilters, setCreatorFilters] = useState(initialFilters.creator);
  const [technicianFilters, setTechnicianFilters] = useState(initialFilters.technician);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const { isAdminOrTechnician, loading: userLoading, userRole } = useUser();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const canLoadUsers = userRole === 'Admin' || userRole === 'Technician';
      const requests = [
        apiService.getTickets(),
        apiService.getCategories(),
      ];
      if (canLoadUsers) {
        requests.push(apiService.getUsers());
      }

      const results = await Promise.all(requests);
      const ticketsData = results[0];
      const categoriesData = results[1];
      const usersData = canLoadUsers ? results[2] : [];

      setTickets(ticketsData);
      setCategories(categoriesData);
      setUsers(usersData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  useEffect(() => {
    if (userLoading) return;
    loadData();
  }, [userLoading, loadData]);

  useEffect(() => {
    saveTicketListFilters(DASHBOARD_TICKET_FILTERS_STORAGE_KEY, {
      status: statusFilters,
      category: categoryFilters,
      creator: creatorFilters,
      technician: technicianFilters,
    });
  }, [statusFilters, categoryFilters, creatorFilters, technicianFilters]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 1: return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 2: return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 3: return <ClockIcon className="h-4 w-4 text-orange-500" />;
      case 4: return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 5: return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'New';
      case 2: return 'In Progress';
      case 3: return 'Waiting Feedback';
      case 4: return 'Closed';
      case 5: return 'Reopened';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'bg-blue-100 text-blue-800';
      case 2: return 'bg-yellow-100 text-yellow-800';
      case 3: return 'bg-orange-100 text-orange-800';
      case 4: return 'bg-green-100 text-green-800';
      case 5: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 3: return 'bg-yellow-100 text-yellow-800';
      case 4: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      case 4: return 'Critical';
      default: return 'Unknown';
    }
  };

  // Status options for multi-select
  const statusOptions = [
    { value: 1, label: 'New' },
    { value: 2, label: 'In Progress' },
    { value: 3, label: 'Waiting Feedback' },
    { value: 4, label: 'Closed' },
    { value: 5, label: 'Reopened' }
  ];

  // Category options for multi-select
  const categoryOptions = categories.map(category => ({
    value: category.id,
    label: category.name
  }));

  // Creator options for multi-select (users who created tickets)
  const creatorOptions = users.map(user => ({
    value: user.id,
    label: user.name
  }));

  // Technician options for multi-select (technicians and admins)
  const technicianOptions = users
    .filter(user => user.role === 2 || user.role === 3) // Technician or Admin
    .map(user => ({
      value: user.id,
      label: `${user.name} (${user.role === 2 ? 'Technician' : 'Admin'})`
    }));



    const filteredTickets = tickets.filter(ticket => {
    const statusMatch = statusFilters.length === 0 || statusFilters.includes(ticket.status);
    const categoryMatch = categoryFilters.length === 0 || categoryFilters.includes(ticket.categoryId);
    
    // Handle empty strings and null values for userId and technicianId
    const ticketUserId = ticket.userId && ticket.userId.trim() !== '' ? ticket.userId : null;
    const ticketTechnicianId = ticket.technicianId && ticket.technicianId.trim() !== '' ? ticket.technicianId : null;
    
    // Ensure boolean results for filtering - convert null to false
    const creatorMatch = creatorFilters.length === 0 || Boolean(ticketUserId && creatorFilters.includes(ticketUserId));
    const technicianMatch = technicianFilters.length === 0 || Boolean(ticketTechnicianId && technicianFilters.includes(ticketTechnicianId));
    
    return statusMatch && categoryMatch && creatorMatch && technicianMatch;
  });

  const stats = {
    total: tickets.length,
    new: tickets.filter(t => t.status === 1).length,
    inProgress: tickets.filter(t => t.status === 2).length,
    waitingFeedback: tickets.filter(t => t.status === 3).length,
    closed: tickets.filter(t => t.status === 4).length,
    reopened: tickets.filter(t => t.status === 5).length,
    slaBreached: tickets.filter(t => t.isSlaBreached).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isAdminOrTechnician() ? 'Ticket Dashboard' : 'My Tickets'}
          </h1>
          <p className="text-gray-600">
            {isAdminOrTechnician() 
              ? 'Manage and track IT support tickets' 
              : 'View and manage your support tickets'
            }
          </p>
        </div>
        <Link
          to="/new-ticket"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Ticket
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Tickets</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">New</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.new}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.inProgress}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-orange-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Waiting Feedback</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.waitingFeedback}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Closed</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.closed}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Reopened</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.reopened}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">SLA Breached</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.slaBreached}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <MultiSelect
              options={statusOptions}
              selectedValues={statusFilters}
              onChange={setStatusFilters}
              placeholder="Select statuses..."
              label="Filter by Status"
            />
          </div>
          <div>
            <MultiSelect
              options={categoryOptions}
              selectedValues={categoryFilters}
              onChange={setCategoryFilters}
              placeholder="Select categories..."
              label="Filter by Category"
            />
          </div>
          <div>
            <MultiSelect
              options={creatorOptions}
              selectedValues={creatorFilters}
              onChange={setCreatorFilters}
              placeholder="Select creators..."
              label="Filter by Creator"
            />
          </div>
          <div>
            <MultiSelect
              options={technicianOptions}
              selectedValues={technicianFilters}
              onChange={setTechnicianFilters}
              placeholder="Select technicians..."
              label="Filter by Technician/Admin"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={loadData}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {isAdminOrTechnician() ? 'Recent Tickets' : 'My Tickets'}
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredTickets.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="text-gray-500">No tickets found matching your filters.</p>
            </div>
          ) : (
            filteredTickets.map(ticket => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                getStatusIcon={getStatusIcon}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getPriorityColor={getPriorityColor}
                getPriorityText={getPriorityText}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 